import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Pill,
} from 'lucide-react';
import AppShell from '../layouts/AppShell';
import ShiftGate from '../components/ShiftGate';
import { getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import { patientFluidBalancePath, ROUTES } from '../constants/routes';
import { useShift } from '../contexts/ShiftContext';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import CarePlansTab from '../components/carePlan/CarePlansTab';
import { translateWard } from '../i18n/translations';
import {
  translateSex,
} from '../i18n/displayLabels';
import TaskManagedCard from '../components/TaskManagedCard';
import TaskDocumentationModal from '../components/TaskDocumentationModal';
import {
  isMedicationTask,
  isPrnTask,
} from '../utils/carePlanTasks';
import { groupTasksByShiftBlocks } from '../utils/shiftTimeBlocks';

function PrnTabIcon({ className }) {
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center text-[13px] font-normal leading-none text-gray-900 ${className || ''}`}
      aria-hidden="true"
    >
      (
      <Check className="h-3 w-3" strokeWidth={2} />
      )
    </span>
  );
}

function TaskTabs({ active, onChange, t }) {
  const tabs = [
    { id: 'all', label: t('all'), Icon: ClipboardCheck },
    { id: 'medications', label: t('medications'), Icon: Pill },
    { id: 'prn', label: t('prnAsNeeded'), Icon: PrnTabIcon },
  ];

  return (
    <div className="flex items-stretch border-b border-gray-200">
      {tabs.map((tab) => {
        const Icon = tab.Icon;
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`flex flex-1 flex-col items-center gap-1 px-1 pb-2.5 pt-1 text-[11px] font-medium transition-colors sm:text-xs ${
              isActive
                ? 'border-b-2 border-gray-900 text-gray-900'
                : 'border-b-2 border-transparent text-gray-500'
            }`}
          >
            <Icon className="h-5 w-5 text-gray-900" strokeWidth={1.75} />
            <span className="truncate">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function sortActiveTasks(taskList) {
  const now = new Date();
  const withMeta = taskList.map((task) => {
    const due = task.dueTime ? new Date(task.dueTime) : null;
    const diffMin = due ? Math.round((due.getTime() - now.getTime()) / 60000) : null;
    const overdueBy =
      task.status === 'overdue'
        ? Math.max(
            1,
            Math.round((now.getTime() - new Date(task.windowEnd || task.dueTime || now).getTime()) / 60000)
          )
        : diffMin != null && diffMin < 0
          ? Math.abs(diffMin)
          : 0;

    let group = 3;
    if (task.status === 'overdue' || (diffMin != null && diffMin < 0)) group = 0;
    else if (diffMin != null && diffMin <= 0) group = 1;
    else if (diffMin != null && diffMin <= 60) group = 1;
    else if (task.status === 'ongoing' || task.status === 'in_progress' || !task.dueTime) group = 2;
    else group = 3;

    return { task, group, overdueBy, diffMin: diffMin ?? Number.MAX_SAFE_INTEGER };
  });

  return withMeta
    .sort((a, b) => {
      if (a.group !== b.group) return a.group - b.group;
      if ((a.task.priority === 'high') !== (b.task.priority === 'high')) {
        return a.task.priority === 'high' ? -1 : 1;
      }
      if (a.group === 0) return b.overdueBy - a.overdueBy;
      return a.diffMin - b.diffMin;
    })
    .map((x) => x.task);
}

function ShiftTaskSections({
  sections,
  onToggleComplete,
  onOpenDocumentation,
  onOpenFluidBalance,
  emptyLabel,
  t,
}) {
  return (
    <div className="space-y-1">
      {sections.map((section) => (
        <div key={section.id}>
          <div className="sticky top-0 z-10 -mx-4 border-b border-[#c5d4e8] bg-[#eceff3] px-4 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-900">
                {t(section.nameKey)}
              </h3>
              <p className="shrink-0 text-xs font-bold text-gray-900">
                {section.timeRange}
              </p>
            </div>
          </div>
          {section.tasks.length === 0 ? (
            <p className="px-1 py-3 text-sm text-gray-500">{emptyLabel}</p>
          ) : (
            <div>
              {section.tasks.map((task) => (
                <TaskManagedCard
                  key={task.id}
                  task={task}
                  onToggleComplete={onToggleComplete}
                  onOpenDocumentation={onOpenDocumentation}
                  onOpenFluidBalance={onOpenFluidBalance}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SectionToggle({ title, open, onToggle, children }) {
  return (
    <section className="card-surface overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <h2 className="text-sm font-bold text-gray-900">{title}</h2>
        {open ? (
          <ChevronUp className="h-4 w-4 text-gray-500" strokeWidth={2} />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" strokeWidth={2} />
        )}
      </button>
      {open ? <div className="border-t border-gray-100 px-3 py-3">{children}</div> : null}
    </section>
  );
}

export default function PatientDetailScreen() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOnShift } = useShift();
  const { t, language } = useLanguage();
  const {
    admittedPatients,
    getPatientTasks,
    setPatientTasks,
    ensureDefaultRecurringTasks,
    getPatientCarePlans,
    addCarePlanAddendum,
    resolveCarePlan,
    taskDocSession,
    openTaskDocumentation,
    closeTaskDocumentation,
  } = useSession();
  const patient = getPatientById(id, admittedPatients);
  const tasks = getPatientTasks(id) || [];
  const carePlans = getPatientCarePlans(id) || [];
  const [taskTab, setTaskTab] = useState('all');
  const [resolvingCarePlanId, setResolvingCarePlanId] = useState(null);
  const [savingCarePlanAddendumId, setSavingCarePlanAddendumId] = useState(null);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [carePlansOpen, setCarePlansOpen] = useState(false);
  const mergedTasksForPatientRef = useRef(null);

  useEffect(() => {
    if (!patient?.id || !isOnShift) {
      mergedTasksForPatientRef.current = null;
      return;
    }
    if (mergedTasksForPatientRef.current === patient.id) return;
    mergedTasksForPatientRef.current = patient.id;
    ensureDefaultRecurringTasks(patient, new Date());
  }, [patient?.id, isOnShift, ensureDefaultRecurringTasks, patient]);

  useEffect(() => {
    // Soft-close task documentation when leaving this patient so the FAB
    // cannot keep forcing vitals/BP onto free recordings.
    return () => {
      closeTaskDocumentation?.();
    };
  }, [closeTaskDocumentation, id]);

  useEffect(() => {
    if (location.state?.taskTab) {
      const next = location.state.taskTab === 'pending' || location.state.taskTab === 'completed'
        ? 'all'
        : location.state.taskTab;
      if (['all', 'medications', 'prn'].includes(next)) {
        setTaskTab(next);
      }
      if (location.state.taskTab === 'flowsheet' || location.state.taskTab === 'notes') {
        navigate(patientFluidBalancePath(id), { replace: true });
        return;
      }
      if (location.state.taskTab === 'careplans') {
        setCarePlansOpen(true);
      }
    }
  }, [id, location.state, navigate]);

  const activeTasks = useMemo(
    () =>
      sortActiveTasks(
        tasks.filter((item) =>
          ['pending', 'overdue', 'ongoing', 'in_progress'].includes(item.status)
        )
      ),
    [tasks]
  );

  const completedTasks = useMemo(
    () =>
      [...tasks.filter((item) => item.status === 'complete')].sort((a, b) =>
        String(b.completedAt || '').localeCompare(String(a.completedAt || ''))
      ),
    [tasks]
  );

  const tabTasksForShiftGrouping = useMemo(() => {
    if (taskTab === 'medications') {
      return tasks.filter(isMedicationTask);
    }
    if (taskTab === 'all') {
      return tasks;
    }
    return [];
  }, [tasks, taskTab]);

  const shiftSections = useMemo(
    () => (taskTab === 'prn' ? [] : groupTasksByShiftBlocks(tabTasksForShiftGrouping)),
    [taskTab, tabTasksForShiftGrouping]
  );

  const tabActiveTasks = useMemo(() => {
    if (taskTab === 'prn') {
      return activeTasks.filter((task) => isPrnTask(task) && !isMedicationTask(task));
    }
    return [];
  }, [activeTasks, taskTab]);

  const tabCompletedTasks = useMemo(() => {
    if (taskTab === 'prn') {
      return completedTasks.filter((task) => isPrnTask(task) && !isMedicationTask(task));
    }
    return [];
  }, [completedTasks, taskTab]);

  const handleToggleComplete = (taskId) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task) return;

    const completionMode = task.completionMode || 'manual';
    if (completionMode !== 'manual' && task.status !== 'complete') return;

    setPatientTasks(id, (prev) =>
      prev.map((item) => {
        if (item.id !== taskId) return item;
        if ((item.completionMode || 'manual') !== 'manual' && item.status !== 'complete') return item;
        return {
          ...item,
          status: item.status === 'complete' ? 'pending' : 'complete',
          completedAt: item.status === 'complete' ? null : new Date().toISOString(),
          completedByNoteId: item.status === 'complete' ? null : 'manual',
        };
      })
    );
  };

  const handleOpenDocumentation = (task) => {
    openTaskDocumentation(id, task);
  };

  const handleOpenFluidBalance = () => {
    navigate(patientFluidBalancePath(id));
  };

  if (!patient) {
    return (
      <AppShell>
        <div className="p-8 text-center text-gray-500">{t('patientNotFound')}</div>
      </AppShell>
    );
  }
  const displayName = getPatientDisplayName(patient, language);
  const initial = displayName.charAt(0).toUpperCase();

  const handleCarePlanAddendum = (carePlanId, payload) => {
    setSavingCarePlanAddendumId(carePlanId);
    addCarePlanAddendum(id, carePlanId, payload);
    setSavingCarePlanAddendumId(null);
  };

  const handleResolveCarePlan = (carePlanId) => {
    setResolvingCarePlanId(carePlanId);
    resolveCarePlan(id, carePlanId);
    setResolvingCarePlanId(null);
  };

  const handleExportCarePlanPdf = async (carePlan) => {
    const { generateCarePlanPDF } = await import('../services/carePlanPdf');
    generateCarePlanPDF({
      carePlan,
      patientName: displayName,
      room: patient.room,
      language,
    });
  };

  return (
    <AppShell showFab={isOnShift}>
      <header className="bg-page px-4 pb-2 pt-4">
        <button
          type="button"
          onClick={() => navigate(ROUTES.PATIENTS)}
          className="flex h-10 w-10 items-center justify-center rounded-full text-gray-800"
          aria-label={t('backToPatients')}
        >
          <ArrowLeft className="h-6 w-6" strokeWidth={2} />
        </button>
      </header>

      <ShiftGate>
        <div className="space-y-4 px-4 pb-4">
          <div className="card-surface p-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-mid text-xl font-bold text-primary">
                {initial}
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-gray-900" dir="auto">
                  {displayName}
                </h1>
                <p className="mt-0.5 text-sm text-gray-500">
                  {t('room')} {patient.room} · {t('age')} {patient.age} · {translateSex(t, patient.sex)}
                </p>
                <p className="text-sm text-gray-500">{translateWard(t, patient.ward)}</p>
              </div>
            </div>
          </div>

          <section className="card-surface space-y-3 p-4">
            <TaskTabs active={taskTab} onChange={setTaskTab} t={t} />

            {taskTab === 'prn' ? (
              <>
                {tabActiveTasks.length === 0 ? (
                  <p className="py-2 text-center text-sm text-gray-500">{t('noPrnTasks')}</p>
                ) : (
                  <div>
                    {tabActiveTasks.map((task) => (
                      <TaskManagedCard
                        key={task.id}
                        task={task}
                        onToggleComplete={handleToggleComplete}
                        onOpenDocumentation={handleOpenDocumentation}
                        onOpenFluidBalance={handleOpenFluidBalance}
                      />
                    ))}
                  </div>
                )}

                {tabCompletedTasks.length > 0 && (
                  <div className="border-t border-gray-100 pt-2">
                    <button
                      type="button"
                      onClick={() => setCompletedOpen((open) => !open)}
                      className="flex w-full items-center justify-between py-2 text-sm font-semibold text-gray-600"
                    >
                      <span>
                        {t('completed')} ({tabCompletedTasks.length})
                      </span>
                      {completedOpen ? (
                        <ChevronUp className="h-4 w-4" strokeWidth={2} />
                      ) : (
                        <ChevronDown className="h-4 w-4" strokeWidth={2} />
                      )}
                    </button>
                    {completedOpen && (
                      <div>
                        {tabCompletedTasks.map((task) => (
                          <TaskManagedCard
                            key={task.id}
                            task={task}
                            onToggleComplete={handleToggleComplete}
                            onOpenDocumentation={handleOpenDocumentation}
                            onOpenFluidBalance={handleOpenFluidBalance}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <ShiftTaskSections
                sections={shiftSections}
                onToggleComplete={handleToggleComplete}
                onOpenDocumentation={handleOpenDocumentation}
                onOpenFluidBalance={handleOpenFluidBalance}
                emptyLabel={t('noTasksScheduled')}
                t={t}
              />
            )}
          </section>

          <SectionToggle
            title={t('carePlans')}
            open={carePlansOpen}
            onToggle={() => setCarePlansOpen((open) => !open)}
          >
            <div className="rounded-xl bg-[#f9fafb] px-1 py-2">
              <CarePlansTab
                carePlans={carePlans}
                onAddAddendum={handleCarePlanAddendum}
                onResolve={handleResolveCarePlan}
                onExportPdf={handleExportCarePlanPdf}
                resolvingId={resolvingCarePlanId}
                savingAddendumId={savingCarePlanAddendumId}
              />
            </div>
          </SectionToggle>
        </div>
      </ShiftGate>

      {taskDocSession?.patientId === id && taskDocSession?.open === true ? (
        <TaskDocumentationModal
          session={taskDocSession}
          patient={patient}
          onClose={closeTaskDocumentation}
        />
      ) : null}
    </AppShell>
  );
}
